/**
 * @fileoverview Singer event page component.
 * @summary Main page for singers to search songs and manage their queue.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/SingerEventPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect, useState, useCallback } from 'react'
import { useParams, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faCheck } from '@fortawesome/free-solid-svg-icons'
import { useAppDispatch, useAppSelector } from '../../hooks/redux.js'
import { searchSongs, searchSongsMusicBrainz, clearSearchResults, addCustomSong, type Song } from '../../store/slices/songsSlice.js'
import { fetchQueue, addSongToQueue } from '../../store/slices/queueSlice.js'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'
import { SubscriptionType, getPlanLimits, POLLING_INTERVAL_MS } from '../../config/constants.js'
import Spinner from '../../components/Spinner.js'
import PageHeader from '../../components/PageHeader.js'
import SongSearchForm from './components/SongSearchForm.js'
import SearchResults from './components/SearchResults.js'
import AddCustomSongModal from './components/AddCustomSongModal.js'
import MyQueueSection from './components/MyQueueSection.js'
import FullQueueSection from './components/FullQueueSection.js'
import EventInactiveMessage from './components/EventInactiveMessage.js'

/**
 * @summary Event info interface.
 */
interface EventInfo {
  id: string
  name: string
  description?: string
  date: string
  status: string
  venueName?: string
}

/**
 * @summary Singer event page component.
 * @returns {React.ReactElement} Singer event page.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function SingerEventPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { eventId } = useParams<{ eventId: string }>()
  const { user, isAuthenticated } = useAppSelector((state) => state.auth)
  const { searchResults, isSearching, error: searchError, canLoadMore } = useAppSelector((state) => state.songs)
  const { items: queueItems, isLoading: isLoadingQueue } = useAppSelector((state) => state.queue)

  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null)
  const [isLoadingEvent, setIsLoadingEvent] = useState(true)
  const [eventError, setEventError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [artistQuery, setArtistQuery] = useState('')
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [addingToQueue, setAddingToQueue] = useState<string | null>(null)
  const [addSuccess, setAddSuccess] = useState<string | null>(null)
  const [addError, setAddError] = useState<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [showAddCustomModal, setShowAddCustomModal] = useState(false)
  const [isAddingCustom, setIsAddingCustom] = useState(false)

  // Get guest info from localStorage
  const guestSessionToken = localStorage.getItem('guestSessionToken')
  const guestPseudo = localStorage.getItem('guestPseudo')
  const isGuest = !isAuthenticated && !!guestSessionToken

  // Current user pseudo
  const currentPseudo = isAuthenticated ? user?.profile?.pseudo || user?.profile?.firstName || 'Chanteur' : guestPseudo || 'Invité'

  // Get event info from navigation state, localStorage (for guests), or API (for authenticated users)
  useEffect(() => {
    const fetchEventInfo = async () => {
      if (!eventId) {
        setEventError(t('singer.eventNotFound'))
        setIsLoadingEvent(false)
        return
      }

      // Try to get event info from navigation state first
      const navState = location.state as {
        eventName?: string
        eventDate?: string
        eventStatus?: string
      } | null

      if (navState?.eventName) {
        // Use info from navigation state
        setEventInfo({
          id: eventId,
          name: navState.eventName,
          date: navState.eventDate || '',
          status: navState.eventStatus || 'active',
        })
        setIsLoadingEvent(false)
        dispatch(fetchQueue(eventId))
        return
      }

      if (isGuest) {
        // Try to get from localStorage for guests
        const storedEventId = localStorage.getItem('guestEventId')
        const storedEventName = localStorage.getItem('guestEventName')

        if (storedEventId === eventId && storedEventName) {
          setEventInfo({
            id: eventId,
            name: storedEventName,
            date: localStorage.getItem('guestEventDate') || '',
            status: localStorage.getItem('guestEventStatus') || 'active',
          })
          setIsLoadingEvent(false)
          dispatch(fetchQueue(eventId))
          return
        }
      }

      // For authenticated users or if no cached data, fetch from API
      if (isAuthenticated || !isGuest) {
        try {
          const response = await apiRequest<{ event: { id: string; name: string; date: string; status: string } }>({
            method: 'GET',
            url: API_ENDPOINTS.EVENTS.PUBLIC_INFO(eventId),
          })

          if (response.success && response.data?.event) {
            setEventInfo({
              id: response.data.event.id,
              name: response.data.event.name,
              date: response.data.event.date,
              status: response.data.event.status,
            })
            
            // Auto-join event for authenticated users (ensures Singer entry exists)
            if (isAuthenticated && user) {
              try {
                await apiRequest({
                  method: 'POST',
                  url: API_ENDPOINTS.EVENTS.JOIN(eventId),
                  data: {
                    pseudo: user.profile?.pseudo || user.profile?.firstName || user.email.split('@')[0],
                    guestMode: false,
                  },
                })
              } catch (joinErr) {
                // Ignore join errors (user might already be joined)
                // console.log('Auto-join attempted:', joinErr)
              }
            }
            
            dispatch(fetchQueue(eventId))
          } else {
            setEventError(response.message || t('singer.eventNotFound'))
          }
        } catch (err: any) {
          setEventError(err.message || t('singer.eventNotFound'))
        } finally {
          setIsLoadingEvent(false)
        }
        return
      }

      // No data available
      setEventError(t('singer.sessionExpired'))
      setIsLoadingEvent(false)
    }

    fetchEventInfo()
  }, [eventId, dispatch, t, location.state, isGuest, isAuthenticated])

  // Polling: Auto-refresh queue every 5 minutes if event is active
  useEffect(() => {
    if (!eventId || !eventInfo || eventInfo.status !== 'active') {
      return
    }

    const intervalId = setInterval(() => {
      dispatch(fetchQueue(eventId))
    }, POLLING_INTERVAL_MS)

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId)
    }
  }, [eventId, eventInfo, dispatch])

  // Perform search
  const handleSearch = useCallback(() => {
    if (showAdvancedSearch) {
      // Advanced search: use title and artist separately
      if (searchQuery.trim().length >= 2 || artistQuery.trim().length >= 2) {
        dispatch(searchSongs({
          title: searchQuery.trim() || undefined,
          artist: artistQuery.trim() || undefined,
          limit: 20,
        }))
      } else {
        dispatch(clearSearchResults())
      }
    } else {
      // Simple search: use query param
      if (searchQuery.trim().length >= 2) {
        dispatch(searchSongs({ query: searchQuery.trim(), limit: 20 }))
      } else {
        dispatch(clearSearchResults())
      }
    }
  }, [searchQuery, artistQuery, showAdvancedSearch, dispatch])

  // Search on Enter or button click
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setHasSearched(true)
    handleSearch()
  }

  // Check if search button should be disabled
  const isSearchDisabled = showAdvancedSearch
    ? (searchQuery.trim().length < 2 && artistQuery.trim().length < 2)
    : searchQuery.trim().length < 2

  // Add song to queue
  const handleAddToQueue = async (song: Song) => {
    if (!eventId) return

    setAddingToQueue(song.id)
    setAddError(null)
    setAddSuccess(null)

    try {
      await dispatch(
        addSongToQueue({
          eventId,
          songId: song.id,
          sessionToken: guestSessionToken || undefined,
        }),
      ).unwrap()

      setAddSuccess(t('singer.songAdded', { title: song.title }))
      // Refresh queue
      dispatch(fetchQueue(eventId))
      // Clear search results
      dispatch(clearSearchResults())
      setSearchQuery('')
      setArtistQuery('')

      // Clear success message after 3 seconds
      setTimeout(() => setAddSuccess(null), 3000)
    } catch (err: any) {
      // With rejectWithValue, error data is in err.payload
      const errorPayload = err.payload || err
      
      // Handle specific error codes with translated messages
      if (errorPayload.code === 'QUEUE_LIMIT_REACHED') {
        const limit = errorPayload.limit ?? getPlanLimits(SubscriptionType.FREE).maxSongsInQueue
        const currentCount = errorPayload.currentCount ?? 0
        setAddError(t('singer.queueLimitReached', { limit, currentCount }))
      } else {
        setAddError(errorPayload.message || err.message || t('singer.addError'))
      }
    } finally {
      setAddingToQueue(null)
    }
  }

  // Add custom song (when no search results): save to cache then add to queue
  const handleAddCustomSong = async (title: string, artist: string) => {
    if (!eventId) return

    setIsAddingCustom(true)
    setAddError(null)

    try {
      const song = await dispatch(addCustomSong({ title, artist })).unwrap()
      await dispatch(
        addSongToQueue({
          eventId,
          songId: song.id,
          sessionToken: guestSessionToken || undefined,
        }),
      ).unwrap()

      setAddSuccess(t('singer.songAdded', { title: song.title }))
      dispatch(clearSearchResults())
      setSearchQuery('')
      setArtistQuery('')
      setShowAddCustomModal(false)
      dispatch(fetchQueue(eventId))
      setTimeout(() => setAddSuccess(null), 3000)
    } catch (err: unknown) {
      const errorPayload = err && typeof err === 'object' && 'payload' in err ? (err as { payload?: { message?: string; code?: string } }).payload : err
      const message = errorPayload && typeof errorPayload === 'object' && errorPayload !== null && 'message' in errorPayload
        ? String((errorPayload as { message?: string }).message)
        : err instanceof Error ? err.message : t('singer.addError')
      setAddError(message)
    } finally {
      setIsAddingCustom(false)
    }
  }

  // Load more results from MusicBrainz
  const handleLoadMore = async () => {
    setIsLoadingMore(true)
    try {
      await dispatch(
        searchSongsMusicBrainz({
          query: showAdvancedSearch ? undefined : searchQuery,
          title: showAdvancedSearch ? searchQuery : undefined,
          artist: showAdvancedSearch ? artistQuery : undefined,
          limit: 50,
        }),
      ).unwrap()
    } catch (err) {
      console.error('Failed to load more results:', err)
    } finally {
      setIsLoadingMore(false)
    }
  }

  // Filter queue items for current user
  const myQueueItems = queueItems.filter((item) => {
    if (isAuthenticated && user) {
      // For authenticated users, check if singer pseudo matches
      return item.singer?.pseudo === currentPseudo
    }
    // For guests, check pseudo
    return item.singer?.pseudo === guestPseudo && item.singer?.guestMode
  })

  // Loading state
  if (isLoadingEvent) {
    return (
      <div className="min-h-screen flex items-center justify-center py-8 px-4">
        <Spinner size="lg" />
      </div>
    )
  }

  // Event not found or error
  if (eventError || !eventInfo) {
    return (
      <div className="min-h-screen flex mt-4 justify-center ">
        <div className="w-full page-container">
          <div className="card text-center">
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-(--color-text-primary) mb-4">{t('common.error')}</h2>
            <p className="text-(--color-text-secondary) mb-6">{eventError}</p>
            <Link to="/" className="btn btn-primary">
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Event not active
  if (eventInfo.status !== 'active') {
    return <EventInactiveMessage eventName={eventInfo.name} status={eventInfo.status} />
  }

  return (
    <div className="min-h-screen">
      <div className="w-full page-container">
        {/* Header */}
        <PageHeader
          title={eventInfo.name}
          subtitle={
            <span className="flex items-center justify-center gap-2">
              <FontAwesomeIcon icon={faUser} className="w-3 h-3" />
              {currentPseudo}
              {isGuest && <span className="text-xs">({t('singer.guestMode')})</span>}
            </span>
          }
        />

        {/* Success/Error messages */}
        {addSuccess && (
          <div className="alert alert-success mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faCheck} />
            <span>{addSuccess}</span>
          </div>
        )}
        {addError && (
          <div className="alert alert-error mb-4 flex items-center gap-2">
            <span>{addError}</span>
          </div>
        )}

        {/* Search Form */}
        <SongSearchForm
          searchQuery={searchQuery}
          artistQuery={artistQuery}
          showAdvancedSearch={showAdvancedSearch}
          isSearching={isSearching}
          isSearchDisabled={isSearchDisabled}
          onSearchQueryChange={(value) => {
            setSearchQuery(value)
            if (value.length < 2 && artistQuery.length < 2) {
              setHasSearched(false)
            }
          }}
          onArtistQueryChange={(value) => {
            setArtistQuery(value)
            if (value.length < 2 && searchQuery.length < 2) {
              setHasSearched(false)
            }
          }}
          onToggleAdvancedSearch={() => {
            setShowAdvancedSearch(!showAdvancedSearch)
            if (!showAdvancedSearch) {
              setHasSearched(false)
            } else {
              setArtistQuery('')
              setHasSearched(false)
            }
          }}
          onSubmit={handleSearchSubmit}
        />

        {/* Search Results */}
        {(searchError || searchResults.length > 0 || (hasSearched && !isSearching)) && (
          <div className="card mb-6">
            <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">
              {t('singer.searchResults')}
            </h2>
            <SearchResults
              results={searchResults}
              isSearching={isSearching}
              searchError={searchError}
              canLoadMore={canLoadMore}
              isLoadingMore={isLoadingMore}
              hasSearched={hasSearched}
              addingToQueue={addingToQueue}
              onAddToQueue={handleAddToQueue}
              onLoadMore={handleLoadMore}
              onAddCustomClick={() => setShowAddCustomModal(true)}
            />
          </div>
        )}

        <AddCustomSongModal
          isOpen={showAddCustomModal}
          initialTitle={searchQuery}
          initialArtist={artistQuery}
          onSubmit={handleAddCustomSong}
          onClose={() => setShowAddCustomModal(false)}
          isSubmitting={isAddingCustom}
        />

        {/* My Queue */}
        <MyQueueSection
          items={myQueueItems}
          isLoading={isLoadingQueue}
          currentPseudo={currentPseudo}
        />

        {/* Full Queue */}
        {eventId && (
          <FullQueueSection
            items={queueItems}
            isLoading={isLoadingQueue}
            eventId={eventId}
            currentPseudo={currentPseudo}
          />
        )}
      </div>
    </div>
  )
}

